import React, {createContext, useContext} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {getErrorMessage, isEmail} from '../../../../common/utils';

export type ForgotPasswordContext = {[key: string]: never};

export type ForgotPasswordContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: ForgotPasswordContext = {};

export type ForgotPasswordFormValues = {
  email: string,
}

export const defaultFormValues: ForgotPasswordFormValues = {
  email: '',
};

export const ForgotPasswordContext = createContext<ForgotPasswordContext>(defaultContext);
ForgotPasswordContext.displayName = 'ForgotPasswordContext';

export const useForgotPasswordContext = () => useContext(ForgotPasswordContext);

export const ForgotPasswordContextProvider = ({children}: ForgotPasswordContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();

  const formik = useFormik<ForgotPasswordFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof ForgotPasswordFormValues]: string }> = {};
      if (!values.email) errors.email = E.requiredField;
      if (values.email && !isEmail(values.email)) errors.email = E.notValidEmail;

      return errors;
    },
    onSubmit: async (values) => {
      const trimmedEmail = values.email ? values.email.trim() : '';
      try {
        const result = await apiService.requestPasswordReset(trimmedEmail);
        if (result.errorCode === 0) {
          globalMessage.open(E.checkLinkInEmail);
        }
        if (result.errorCode < 0) {
          globalMessage.open(getErrorMessage(result.errorCode, E));
        }
      } catch (e) {
        globalMessage.open(E.serverError);
      }
    },
    enableReinitialize: true,
  });

  const context: ForgotPasswordContext = {
    ...defaultContext,
  };

  return (
    <ForgotPasswordContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </ForgotPasswordContext.Provider>
  );
};
